import React from 'react'
import { Link, useParams } from 'react-router-dom'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import IconButton from '@mui/material/IconButton'
import Container from '@mui/material/Container'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'

import { useContextHook } from '../store/context'
import ListHeader from '../components/listheader/listheader'
import { toDateString, toTimeString } from '../utils/dateUtils'
import Alert from '../components/alert/alert'
import { kgToPounds, getBigWeightSuffix } from '../utils/weightUtils'
import { Schedule } from '../types/schedule'
import { ScheduleStatus } from '../types/enums/scheduleStatus'
import { get, setPageTitle } from '../utils/service'
import { PagedResponse } from '../types/pagedResponse'
import ResponsiveTableHeader from '../components/fields/responsiveTableHeader'
import Pagination from '../components/fields/pagination'
import Stack from '@mui/material/Stack'

const ScheduleView = (): JSX.Element => {
  const { state } = useContextHook()
  const params = useParams()
  const theme = useTheme()

  const [isLoadingSchedules, setIsLoadingSchedules] = React.useState<boolean>(false)
  const [schedules, setSchedules] = React.useState<Array<Schedule> | null>(null)
  const [loadSchedulesUrl, setLoadSchedulesUrl] = React.useState<string>('')
  const [paginationSettings, setPaginationSettings] = React.useState<PagedResponse<Schedule> | null>(null)
  const [hasLoadSchedulesError, setHasLoadSchedulesError] = React.useState<boolean>(false)

  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  React.useEffect(() => {
    if (loadSchedulesUrl) {
      setSchedules(null)
      setIsLoadingSchedules(true)
      setHasLoadSchedulesError(false)
      get<PagedResponse<Schedule>>(loadSchedulesUrl)
        .then((response) => {
          setIsLoadingSchedules(false)
          setPaginationSettings(response.data)
          setSchedules(response.data.results)
        })
        .catch(() => {
          setIsLoadingSchedules(false)
          setHasLoadSchedulesError(true)
        })
    }
  }, [loadSchedulesUrl])

  React.useEffect(() => {
    if (state.currentMachine?.name && state.currentMachine?.id && state.currentMachine?.is_p2000) {
      setPageTitle(`${state.currentMachine.name} schedule`)
      setLoadSchedulesUrl(`schedules/?machine=${state.currentMachine.id}`)
    }
  }, [state.currentMachine?.name, state.currentMachine?.id, state.currentMachine?.is_p2000])

  // in progress: mix of scheduled and done
  const currentRoast = schedules
    ? schedules.filter(
        (x) =>
          x.iterations.some((y) => y.status === ScheduleStatus.Scheduled) &&
          x.iterations.some((y) => y.status === ScheduleStatus.Done)
      )
    : []

  // scheduled: where ALL are scheduled
  const scheduledRoasts = schedules
    ? schedules.filter((x) => x.iterations.every((y) => y.status === ScheduleStatus.Scheduled))
    : []

  // done: where ALL are done
  const doneRoasts = schedules
    ? schedules.filter((x) => x.iterations.every((y) => y.status === ScheduleStatus.Done))
    : []

  const getWeightValue = (schedule: Schedule): string => {
    const weightInKg = schedule.iterations.reduce((acc: number, current) => acc + current.weight, 0)
    return `${kgToPounds(weightInKg / 1000).toFixed(2)} ${getBigWeightSuffix()}`
  }

  const generateIterations = (schedule: Schedule): string => {
    const scheduled = schedule.iterations.filter((x) => x.status === ScheduleStatus.Scheduled).length
    const done = schedule.iterations.filter((x) => x.status === ScheduleStatus.Done).length
    return `${done} / ${scheduled + done}`
  }

  const generateTableRow = (schedule: Schedule): JSX.Element => {
    return (
      <TableRow key={schedule.url} className="colored-row">
        <TableCell>
          <Button
            className="link-cell"
            component={Link}
            to={`/machine/${params.machineId}/schedules/${schedule.batch_no}/`}
          />
          <ResponsiveTableHeader header="Batch" />
          {schedule.batch_no}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Date" />
          {schedule.date ? toDateString(schedule.date) : ''}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Start time" />
          {schedule.date ? toTimeString(schedule.date) : ''}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Inventory" />
          {schedule.inventory_data?.name}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Profile" />
          {schedule.profile_data?.name}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Iterations" />
          {generateIterations(schedule)}
        </TableCell>
        <TableCell>
          <ResponsiveTableHeader header="Total weight" />
          {getWeightValue(schedule)}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Container component="main" maxWidth={false}>
      {state.currentMachine && !state.currentMachine.is_p2000 ? (
        <Alert severity="error">Schedule is not supported for current machine</Alert>
      ) : (
        <>
          {state.currentMachine?.name && (
            <ListHeader
              headerText={`${state.currentMachine.name} roast schedule`}
              searchPlaceholder="Search schedule"
              typeString="scheduled roasts"
              totalResults={paginationSettings?.count || 0}
              onSearch={(searchString: string): void => {
                const searchFragment = searchString ? `&search=${searchString}` : ''
                const newLoadUrl = `schedules/?machine=${state.currentMachine?.id}${searchFragment}`
                if (newLoadUrl !== loadSchedulesUrl) {
                  setPaginationSettings(null)
                  setLoadSchedulesUrl(newLoadUrl)
                }
              }}
            >
              <Tooltip title="Add scheduled roast">
                <IconButton
                  component={Link}
                  color="primary"
                  to={`/machine/${state.currentMachine?.slug}/schedules/create`}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </ListHeader>
          )}
          {schedules && (
            <>
              {isScreenBelowMdBreakpoint && (
                <Button
                  variant="contained"
                  component={Link}
                  to={`/machine/${state.currentMachine?.slug}/schedules/create`}
                >
                  Add scheduled roast
                </Button>
              )}
              <TableContainer sx={{ mt: 2 }}>
                <Table className="colored-table collapse-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Start time</TableCell>
                      <TableCell>Inventory</TableCell>
                      <TableCell>Profile</TableCell>
                      <TableCell>Iterations</TableCell>
                      <TableCell>Total weight</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRoast.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={100}>Currently roasting</TableCell>
                      </TableRow>
                    )}
                    {currentRoast.map((x) => generateTableRow(x))}
                    {scheduledRoasts.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={100}>Scheduled roasts</TableCell>
                      </TableRow>
                    )}
                    {scheduledRoasts.map((x) => generateTableRow(x))}
                    {doneRoasts.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={100}>Completed roasts</TableCell>
                      </TableRow>
                    )}
                    {doneRoasts.map((x) => generateTableRow(x))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {schedules && schedules.length === 0 && !isLoadingSchedules && (
            <Alert severity="info">No schedules found</Alert>
          )}
          {isLoadingSchedules && <CircularProgress />}
          <Stack sx={{ mt: 1, mb: 4 }}>
            {paginationSettings && (
              <Pagination
                isLoading={isLoadingSchedules}
                paginationSettings={paginationSettings}
                onChangePage={(newPageUrl: string) => setLoadSchedulesUrl(newPageUrl)}
              />
            )}
          </Stack>
          {hasLoadSchedulesError && <Alert severity="error">Could not load schedules</Alert>}
        </>
      )}
    </Container>
  )
}

export default ScheduleView
